import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  BlockedOnChip,
  blockedOnParty,
  daysBlocked,
  foldPayers,
  mergeStatusAndEvidence,
  PARTY_ORDER,
  PAYER_LOAD_PAGE_SIZE,
  payerLoadFailure,
  RosterChipStrip,
  StediStatusList,
  toStediStatusView,
  uniqueEvidencePayerIds,
  type BlockedParty,
  type PayerLoadFailure,
  type PayerLoadOrganizationResponse,
  type PayerLoadOrganizationsResponse,
} from '@features/payer-connections';
import {
  useCancelDepartedPayerEvidence,
  usePayerLoadOrganizationsQuery,
  usePayerOrganizationEvidenceQuery,
  usePayerOrganizationStatusQuery,
} from '@features/payer-connections/store';
import { Hub } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { BackButton } from '../components/back-button';
import { formatDateTime, getStalenessColor } from '../utils/formatting';

type ConnectedWithin = PayerLoadOrganizationsResponse['effective_connected_within'];

const SCOPE_LABELS: Record<ConnectedWithin, string> = {
  '30d': 'Connected in last 30 days',
  '90d': 'Connected in last 90 days',
  all: 'All connection dates',
  unknown: 'No recorded connection date',
};

const VALID_SCOPES = Object.keys(SCOPE_LABELS) as ConnectedWithin[];
const MOUNT_FRESHNESS_SECONDS = 30;
const MAX_SEARCH_LENGTH = 100;

interface RosterPriority {
  blockedParty: BlockedParty;
  daysBlocked: number | null;
}

function formatDaysBlocked(days: number | null): string {
  return days == null ? '—' : `${days}d`;
}

function connectedAtLine(connectedAt: string | null): string {
  return connectedAt ? `Connected ${formatDateTime(connectedAt)}` : 'No recorded connection date';
}

function parseScope(value: string | null): ConnectedWithin {
  return VALID_SCOPES.includes(value as ConnectedWithin) ? (value as ConnectedWithin) : '30d';
}

function parsePage(value: string | null): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? Math.min(parsed, 100_000) : 1;
}

function normalizeSearch(value: string | null): string {
  return (value ?? '').trim().slice(0, MAX_SEARCH_LENGTH);
}

export function sortPageByUrgency(
  organizations: PayerLoadOrganizationResponse[],
  priorities: Record<string, RosterPriority | undefined>,
): PayerLoadOrganizationResponse[] {
  return organizations.slice().sort((a, b) => {
    const aPriority = priorities[a.organization_id];
    const bPriority = priorities[b.organization_id];
    if (aPriority && !bPriority) return -1;
    if (!aPriority && bPriority) return 1;
    if (aPriority && bPriority) {
      const partyDifference =
        PARTY_ORDER.indexOf(aPriority.blockedParty) - PARTY_ORDER.indexOf(bPriority.blockedParty);
      if (partyDifference !== 0) return partyDifference;
      if (aPriority.daysBlocked !== bPriority.daysBlocked) {
        if (aPriority.daysBlocked == null) return 1;
        if (bPriority.daysBlocked == null) return -1;
        return bPriority.daysBlocked - aPriority.daysBlocked;
      }
    }
    return a.organization_name.localeCompare(b.organization_name);
  });
}

function requestStageText(
  stage: 'organization list' | 'status' | 'evidence',
  failure: PayerLoadFailure,
) {
  const classification =
    failure.failureClass === 'timeout'
      ? 'timed out'
      : failure.failureClass === 'transport'
        ? 'could not reach the service'
        : 'returned an error';
  return `${stage[0].toUpperCase()}${stage.slice(1)} request ${classification}. ${failure.message}`;
}

interface ProgressiveOrganizationRowProps {
  organization: PayerLoadOrganizationResponse;
  onPriorityChange: (organizationId: string, priority: RosterPriority | null) => void;
}

export function ProgressiveOrganizationRow({
  organization,
  onPriorityChange,
}: ProgressiveOrganizationRowProps) {
  const {
    data: statusData,
    error: statusError,
    isLoading: isStatusLoading,
    isFetching: isStatusFetching,
    refetch: refetchStatus,
  } = usePayerOrganizationStatusQuery({ organizationId: organization.organization_id });

  const statusRows = useMemo(() => statusData?.enrollments ?? [], [statusData?.enrollments]);
  const payerIds = useMemo(() => uniqueEvidencePayerIds(statusRows), [statusRows]);
  const evidenceSignature = useMemo(
    () => `${statusData?.checked_at ?? ''}:${payerIds.join(',')}`,
    [payerIds, statusData?.checked_at],
  );
  const evidenceArgs = useMemo(
    () => ({
      evidenceSignature,
      organizationId: organization.organization_id,
      payerIds,
    }),
    [evidenceSignature, organization.organization_id, payerIds],
  );
  const canLoadEvidence = Boolean(
    statusData && !statusData.error && statusData.connected && payerIds.length > 0,
  );
  const {
    data: evidenceData,
    isFetching: isEvidenceFetching,
    refetch: refetchEvidence,
  } = usePayerOrganizationEvidenceQuery(evidenceArgs, { skip: !canLoadEvidence });
  const evidence = useMemo(() => evidenceData?.evidence ?? [], [evidenceData?.evidence]);
  const evidenceFailure = canLoadEvidence ? (evidenceData?.failure ?? null) : null;
  const completedEvidenceSignature = evidenceData?.completedSignature ?? null;
  const isEvidenceLoading = isEvidenceFetching || Boolean(evidenceData?.isLoading);

  const enrichedRows = useMemo(
    () => mergeStatusAndEvidence(statusRows, evidence),
    [evidence, statusRows],
  );
  const fold = useMemo(() => foldPayers(enrichedRows), [enrichedRows]);
  const hasIncompleteEvidence = enrichedRows.some(
    row =>
      Boolean(row.stedi_payer_id?.trim()) &&
      (row.has_received_claim == null || Boolean(row.evidence_error)),
  );
  const party = blockedOnParty(fold.payers);
  const asOf = statusData?.checked_at ? new Date(statusData.checked_at) : null;
  const blockedDays = asOf && !Number.isNaN(asOf.getTime()) ? daysBlocked(fold.payers, asOf) : null;
  const hasUsableStatus = Boolean(statusData && !statusData.error);

  useEffect(() => {
    onPriorityChange(
      organization.organization_id,
      hasUsableStatus ? { blockedParty: party, daysBlocked: blockedDays } : null,
    );
  }, [blockedDays, hasUsableStatus, onPriorityChange, organization.organization_id, party]);

  const actionCell = (
    <TableCell align="right">
      <Stack spacing={0.75} sx={{ alignItems: 'flex-end' }}>
        <Button
          size="small"
          onClick={() => void refetchStatus()}
          disabled={isStatusFetching || isEvidenceLoading}
        >
          Refresh
        </Button>
        <Button
          component={Link}
          to={`/admin-v2/organization/${organization.organization_id}?tab=intake-hub&intakeSection=payer-connections`}
          size="small"
          variant="outlined"
          sx={{ whiteSpace: 'nowrap' }}
        >
          Open clinic
        </Button>
      </Stack>
    </TableCell>
  );

  return (
    <TableRow data-testid={`payer-organization-${organization.organization_id}`}>
      <TableCell sx={{ minWidth: 190 }}>
        <Typography sx={{ fontWeight: 600 }}>{organization.organization_name}</Typography>
        <Typography variant="caption" color="text.secondary">
          {connectedAtLine(organization.connected_at)}
        </Typography>
      </TableCell>

      {!hasUsableStatus ? (
        <>
          <TableCell colSpan={4}>
            {isStatusLoading ? (
              <Stack spacing={0.75} data-testid="status-loading">
                <Skeleton width="42%" />
                <Skeleton width="68%" />
              </Stack>
            ) : statusData?.error ? (
              <Alert
                severity="error"
                action={
                  <Button size="small" color="inherit" onClick={() => void refetchStatus()}>
                    Retry status
                  </Button>
                }
              >
                Status response returned an organization error. {statusData.error}
              </Alert>
            ) : (
              <Alert
                severity="error"
                action={
                  <Button size="small" color="inherit" onClick={() => void refetchStatus()}>
                    Retry status
                  </Button>
                }
              >
                {requestStageText('status', payerLoadFailure(statusError))}
              </Alert>
            )}
          </TableCell>
          {actionCell}
        </>
      ) : (
        <>
          <TableCell>
            {!statusData?.connected ? (
              <Typography color="text.secondary">Not connected</Typography>
            ) : statusRows.length === 0 ? (
              <Typography color="text.secondary">No enrollments</Typography>
            ) : party === 'NONE' && fold.unknownCount > 0 ? (
              <Typography color="text.secondary">Receipt unknown</Typography>
            ) : (
              <BlockedOnChip party={party} />
            )}
          </TableCell>
          <TableCell>
            {blockedDays == null ? (
              <Tooltip title="No submitted or created timestamp is available for the leading payer.">
                <span className={getStalenessColor(null)}>—</span>
              </Tooltip>
            ) : (
              <span className={getStalenessColor(blockedDays)}>
                {formatDaysBlocked(blockedDays)}
              </span>
            )}
          </TableCell>
          <TableCell sx={{ minWidth: 210 }}>
            {!statusData?.connected ? (
              <Typography variant="body2" color="text.secondary">
                No Stedi connection is configured.
              </Typography>
            ) : statusRows.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No enrollment rows returned.
              </Typography>
            ) : (
              <RosterChipStrip
                payers={fold.payers}
                liveCount={fold.liveCount}
                unknownCount={fold.unknownCount}
              />
            )}
            {isEvidenceLoading && (
              <Typography variant="caption" color="text.secondary" role="status">
                Checking receipt evidence…
              </Typography>
            )}
            {evidenceFailure && (
              <Box
                role="alert"
                sx={{
                  alignItems: 'baseline',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.5,
                  mt: 0.5,
                }}
              >
                <Typography variant="caption" color="warning.dark">
                  {requestStageText('evidence', evidenceFailure)}
                </Typography>
                <Button
                  size="small"
                  color="warning"
                  disabled={isEvidenceLoading}
                  onClick={() => void refetchEvidence()}
                  sx={{ minWidth: 0, p: 0 }}
                >
                  Retry evidence
                </Button>
              </Box>
            )}
            {!evidenceFailure &&
              !isEvidenceLoading &&
              completedEvidenceSignature === evidenceSignature &&
              hasIncompleteEvidence && (
                <Box
                  role="alert"
                  sx={{
                    alignItems: 'baseline',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    mt: 0.5,
                  }}
                >
                  <Typography variant="caption" color="warning.dark">
                    Receipt evidence is incomplete.
                  </Typography>
                  <Button
                    size="small"
                    color="warning"
                    onClick={() => void refetchEvidence()}
                    sx={{ minWidth: 0, p: 0 }}
                  >
                    Retry evidence
                  </Button>
                </Box>
              )}
            {statusError && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {requestStageText('status', payerLoadFailure(statusError))} Previous status remains
                visible.
              </Alert>
            )}
            {isStatusFetching && !isStatusLoading && (
              <Typography variant="caption" color="text.secondary" role="status">
                Refreshing status…
              </Typography>
            )}
          </TableCell>
          <TableCell sx={{ minWidth: 210 }}>
            <StediStatusList
              statuses={statusRows.map(toStediStatusView)}
              showPayerName
              collapsedItemCount={1}
              ariaLabel={`Stedi statuses for ${organization.organization_name}`}
            />
          </TableCell>
          {actionCell}
        </>
      )}
    </TableRow>
  );
}

export default function PayerConnections() {
  const [searchParams, setSearchParams] = useSearchParams();
  const connectedWithin = parseScope(searchParams.get('connectedWithin'));
  const page = parsePage(searchParams.get('page'));
  const appliedSearch = normalizeSearch(searchParams.get('search'));
  const [searchInput, setSearchInput] = useState(appliedSearch);
  const [priorities, setPriorities] = useState<Record<string, RosterPriority | undefined>>({});

  useEffect(() => setSearchInput(appliedSearch), [appliedSearch]);

  useEffect(() => {
    if (searchInput.trim() === appliedSearch) return;
    const timer = setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      const value = normalizeSearch(searchInput);
      if (value) next.set('search', value);
      else next.delete('search');
      next.set('page', '1');
      setSearchParams(next, { replace: true });
    }, 300);
    return () => clearTimeout(timer);
  }, [appliedSearch, searchInput, searchParams, setSearchParams]);

  const {
    currentData: data,
    error,
    isLoading,
    isFetching,
    refetch,
  } = usePayerLoadOrganizationsQuery(
    {
      connectedWithin,
      search: appliedSearch || undefined,
      page,
      pageSize: PAYER_LOAD_PAGE_SIZE,
    },
    { refetchOnMountOrArgChange: MOUNT_FRESHNESS_SECONDS },
  );

  const handlePriorityChange = useCallback(
    (organizationId: string, priority: RosterPriority | null) => {
      setPriorities(current => {
        const nextPriority = priority ?? undefined;
        const previous = current[organizationId];
        if (
          previous?.blockedParty === nextPriority?.blockedParty &&
          previous?.daysBlocked === nextPriority?.daysBlocked
        ) {
          return current;
        }
        return { ...current, [organizationId]: nextPriority };
      });
    },
    [],
  );

  const organizations = useMemo(
    () => sortPageByUrgency(data?.organizations ?? [], priorities),
    [data?.organizations, priorities],
  );
  const selectedOrganizationIds = useMemo(
    () => organizations.map(organization => organization.organization_id),
    [organizations],
  );
  useCancelDepartedPayerEvidence(selectedOrganizationIds);

  const updateScope = (scope: ConnectedWithin) => {
    const next = new URLSearchParams(searchParams);
    next.set('connectedWithin', scope);
    next.set('page', '1');
    setSearchParams(next);
  };

  const updatePage = (nextPage: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(nextPage));
    setSearchParams(next);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 py-8">
        <BackButton />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2, mb: 1 }}>
          <Hub color="primary" fontSize="large" />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Payer Connections
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review payer setup blockers and claim receipt evidence for connected organizations.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 1,
            boxShadow: 1,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            mb: 2,
            p: 2,
          }}
        >
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel id="payer-connection-scope-label">
              Recorded Stedi connection date
            </InputLabel>
            <Select
              labelId="payer-connection-scope-label"
              label="Recorded Stedi connection date"
              value={connectedWithin}
              onChange={event => updateScope(event.target.value as ConnectedWithin)}
            >
              {VALID_SCOPES.map(scope => (
                <MenuItem key={scope} value={scope}>
                  {SCOPE_LABELS[scope]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Search organizations"
            value={searchInput}
            onChange={event => setSearchInput(event.target.value)}
            slotProps={{ htmlInput: { maxLength: MAX_SEARCH_LENGTH } }}
            sx={{ minWidth: 280 }}
          />
          {isFetching && !isLoading && (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }} role="status">
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Updating organization page…
              </Typography>
            </Stack>
          )}
        </Box>

        {data?.search_overrides_date && appliedSearch && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Search includes matching organizations from all connection dates. Clear search to
            restore {SCOPE_LABELS[connectedWithin].toLowerCase()}.
          </Alert>
        )}

        {error && !data && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => void refetch()}>
                Retry list
              </Button>
            }
          >
            {requestStageText('organization list', payerLoadFailure(error))}
          </Alert>
        )}

        {error && data && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {requestStageText('organization list', payerLoadFailure(error))} The previous page
            remains visible.
          </Alert>
        )}

        <TableContainer sx={{ bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
          <Box
            sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between', p: 2 }}
          >
            <Box>
              <Typography component="h2" variant="h5" sx={{ fontWeight: 700 }}>
                Organizations
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {data
                  ? `${data.total} selected · page ${data.page}`
                  : 'Selecting organizations from local records…'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Sorted by urgency within this page
              </Typography>
            </Box>
            {isLoading && <CircularProgress size={24} />}
          </Box>

          {data && organizations.length === 0 ? (
            <Box sx={{ p: 5, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {data.total > 0 && data.page > 1
                  ? 'No organizations on this page.'
                  : 'No organizations match this connection-date scope and search.'}
              </Typography>
              {data.total > 0 && data.page > 1 && (
                <Button sx={{ mt: 1 }} onClick={() => updatePage(1)}>
                  Go to first page
                </Button>
              )}
            </Box>
          ) : (
            <Table size="small" aria-label="Payer connection organizations">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell>Organization</TableCell>
                  <TableCell>Blocked on</TableCell>
                  <TableCell>Days</TableCell>
                  <TableCell>Payers</TableCell>
                  <TableCell>Stedi status</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {organizations.map(organization => (
                  <ProgressiveOrganizationRow
                    key={organization.organization_id}
                    organization={organization}
                    onPriorityChange={handlePriorityChange}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {data && data.total > 0 && (
          <Stack
            direction="row"
            spacing={2}
            sx={{ alignItems: 'center', justifyContent: 'flex-end', mt: 2 }}
          >
            <Button
              variant="outlined"
              disabled={data.page <= 1}
              onClick={() => updatePage(page - 1)}
            >
              Previous
            </Button>
            <Typography variant="body2">
              Page {data.page} of {Math.max(Math.ceil(data.total / data.page_size), 1)}
            </Typography>
            <Button
              variant="outlined"
              disabled={!data.has_next_page}
              onClick={() => updatePage(page + 1)}
            >
              Next
            </Button>
          </Stack>
        )}
      </main>
    </div>
  );
}
